import React, { createContext, useCallback, useContext } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import { Experiment, Experiments } from '../adobe-target-server-side/types'
import { setSsrExperiments } from '../helpers/ssr-experiments-store'

interface SsrExperimentsProviderProps {
  experiments: Experiments
  children?: React.ReactNode
}

interface SsrExperimentsContextTypes {
  getExperienceIdForExperiment: (experimentName: string) => number
}

export const SsrExperimentsContext = createContext<
  SsrExperimentsContextTypes | undefined
>({
  getExperienceIdForExperiment: () => DEFAULT_EXPERIMENT_NOT_FOUND_ID,
})

const DEFAULT_EXPERIMENT_NOT_FOUND_ID = 0

export const getExperiment = (
  experiments: Experiments,
  experimentName: string,
) =>
  experiments.find(
    (experiment: Experiment) => experiment.name === experimentName,
  )

export const getExperienceId = (experiment: Experiment | undefined) =>
  experiment ? Number(experiment.experienceId) : DEFAULT_EXPERIMENT_NOT_FOUND_ID

export const SsrExperimentsProvider = ({
  experiments,
  children,
}: SsrExperimentsProviderProps) => {
  // Set during render so child hooks see Target assignments before they report UI.
  setSsrExperiments(experiments)

  const getExperienceIdForExperiment = useCallback(
    (experimentName: string) => {
      const experiment = getExperiment(experiments, experimentName)
      // only record experiments that exist in Datadog to make the feature flag view clearer
      if (experiment?.experienceId !== undefined) {
        // replace hyphens with underscores to make querying work correctly in Datadog
        const datadogExperimentName = `experiment_${experimentName.replace(
          /-/g,
          '_',
        )}`
        const datadogExperienceId =
          experiment.experienceId === 0
            ? 'Control'
            : `Variant ${experiment.experienceId}`

        datadogRum.addFeatureFlagEvaluation(
          datadogExperimentName,
          datadogExperienceId,
        )
      }
      return getExperienceId(experiment)
    },
    [experiments],
  )

  return (
    <SsrExperimentsContext.Provider value={{ getExperienceIdForExperiment }}>
      {children}
    </SsrExperimentsContext.Provider>
  )
}

export const useSsrExperiments = (): SsrExperimentsContextTypes => {
  const context = useContext(SsrExperimentsContext)

  if (!context) {
    throw new Error(
      'useSsrExperiments must be used within a SsrExperimentProvider',
    )
  }

  return context
}
